import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldRenderProps } from 'react-final-form';

import { popToast } from 'utilities/popToast';

type Location = {
  city: string;
  latitude: number;
  longitude: number;
  description?: string;
};

type PlacesAutocompleteProps = FieldRenderProps<Location, HTMLElement> & {
  icon?: React.ReactNode;
};

const PlacesAutocomplete = ({
  input: { value, onChange, ...inputProps },
  meta,
  icon,
}: PlacesAutocompleteProps) => {
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [isManuallyTyping, setIsManuallyTyping] = useState<boolean>(false);

  const initializeAutocomplete = useCallback(() => {
    if (!autocomplete.current && input.current) {
      try {
        autocomplete.current = new google.maps.places.Autocomplete(
          input.current,
          {
            componentRestrictions: { country: 'us' },
            types: ['geocode'],
            fields: ['geometry', 'name', 'formatted_address'],
          },
        );
        const onAutocompletePlaceChanged = () => {
          if (!autocomplete.current || !input.current) {
            return;
          }
          const place = autocomplete.current.getPlace();

          if (!place?.geometry?.location) {
            popToast(
              `No geometry available${place?.name ? ` for '${place?.name}'` : ''}`,
              { type: 'error' },
            );

            initializeAutocomplete();
            return;
          }

          const {
            geometry: {
              location: { lat, lng },
            },
            name,
          } = place;

          const formatted_address = place.formatted_address || name || '';

          const location = {
            city: name || '',
            latitude: lat(),
            longitude: lng(),
            description: formatted_address,
          };

          setIsManuallyTyping(false);
          setInputValue(formatted_address);
          onChange(location);
        };

        autocomplete.current.addListener(
          'place_changed',
          onAutocompletePlaceChanged,
        );
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    }
  }, [onChange]);

  useEffect(() => {
    try {
      if (value && !isManuallyTyping) {
        const displayValue = value.description || value.city || '';
        setInputValue(displayValue);
      } else if (!value) {
        setInputValue('');
        setIsManuallyTyping(false);
      }
    } catch (error) {
      console.error('Error setting input value:', error);
    }
  }, [value, isManuallyTyping]);

  useEffect(() => {
    try {
      input.current = document.getElementById('map-search') as HTMLInputElement;
      initializeAutocomplete();
    } catch (error) {
      console.error('Error in useEffect:', error);
    }
  }, [initializeAutocomplete]);

  return (
    <div className="relative">
      <div className="relative flex items-center">
        {icon && <div className="absolute left-3">{icon}</div>}
        <input
          {...inputProps}
          value={inputValue}
          onChange={(e) => {
            try {
              const newValue = e.target.value;

              setIsManuallyTyping(true);
              setInputValue(newValue);

              if (!newValue.trim()) {
                onChange(undefined as Location | undefined);
                return;
              }

              onChange({
                city: newValue,
                latitude: 0,
                longitude: 0,
                description: newValue,
              });
            } catch (error) {
              console.error('Error in onChange:', error);
            }
          }}
          onBlur={() => {
            setIsManuallyTyping(false);
          }}
          type="text"
          id="map-search"
          className={`block w-full rounded-md border-gray-300 pl-10 text-sm ${
            meta?.touched && meta?.error ? 'border-red-500' : ''
          }`}
          placeholder="Enter a city"
          style={{ paddingLeft: icon ? '2.5rem' : undefined }}
        />
      </div>
      {meta?.touched && meta?.error && (
        <div className="absolute text-sm text-red-500">{meta.error}</div>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
